import { gettext } from '@eventbrite/i18n';
import {
    CL_LOCALE,
    CO_LOCALE,
    formatMajorMoney,
    formatNumber,
    GB_LOCALE,
    IE_LOCALE,
    PE_LOCALE,
} from '@eventbrite/intl';
import { $FixMe } from '@eventbrite/ts-utils';

export const PRICING_PATH = '/organizer/pricing/';
export const PACKAGE_ONE = 'package1';
export const PACKAGE_TWO = 'package2';
export const PACKAGE_THREE = 'package3';
export const PACKAGE_ONE_NAME = 'Essentials';
export const PACKAGE_TWO_NAME = 'Professional';
export const PACKAGE_THREE_NAME = 'Premium';
export const ESSENTIALS_LABEL = gettext('Essentials Package');
export const PROFESSIONAL_LABEL = gettext('Professional Package');

export const TICKET_PRICE_LABEL = gettext('Ticket Price');
export const TICKET_QUANTITY_LABEL = gettext('Tickets Sold');
export const RESULTS_HEADLINE = gettext('Your estimated payout');
export const RESULTS_CTA = gettext('Create event');
export const CHANGE_PLAN_CTA = gettext('Change your plan');
export const PASS_ON_FEES_LABEL = gettext('Fee Handling');
export const PASS_ON_FEES_OPTION = gettext('Pass fees on to attendees');
export const PASS_ON_FEES_SECONDARY_TEXT = gettext(
    'Attendees pay ticketing fees',
);
export const ABSORB_FEES_OPTION = gettext('Absorb fees');
export const ABSORB_FEES_SECONDARY_TEXT = gettext(
    'Ticketing fees  are deducted from your payout',
);

export const FEE_HANDLING_TOOLTIP_TEXT = gettext(
    'The majority of organizers who pass fees to their buyers pay nothing out of pocket to use Eventbrite. Visit FAQs below to learn more about passing on fees.',
);
export const ESTIMATE_TOOLTIP_TEXT = gettext(
    "Estimates provided for informational purposes only. Actual totals may vary. Estimates do not include all applicable taxes. Visit FAQs below to learn more about Eventbrite's pricing options.",
);
export const PACKAGE_SELECT_LABEL = gettext('Choose a package');
export const ATTENDEE_PAYS = gettext('Attendee pays');
export const IMPOSSIBLE_PRICE_TITLE = gettext('Oops!');

export const IMPOSSIBLE_PRICE_TEXT = gettext(
    "You can't absorb fees greater than your ticket cost. Please try again.",
);

export const PROFESSIONAL_PACKAGE_DEFAULT_BANNER_TEXT = gettext(
    '<h4>For more advanced ticketing needs try calculating your payout using the <a class="eds-btn--link eds-btn--anchor eds-link" id="assortmentLink">Professional package</a>.</h4>',
);
export const ESSENTIAL_PACKAGE_DEFAULT_BANNER_TEXT = gettext(
    '<h4>For simpler ticketing and lower fees, try calculating your payout using the <a id="assortmentLink" class="eds-btn--link eds-btn--anchor eds-link">Essentials package</a>.</h4>',
);

export type PackageTypes = 'package1' | 'package2' | 'package3';
export const DEFAULT_PACKAGE_TYPES: PackageTypes[] = [
    PACKAGE_ONE,
    PACKAGE_TWO,
    PACKAGE_THREE,
];
export const DEFAULT_PACKAGE_NAMES = {
    package1: PACKAGE_ONE_NAME,
    package2: PACKAGE_TWO_NAME,
    package3: PACKAGE_THREE_NAME,
};

export interface FeeBreakdownInterface {
    attendeeTotal: string;
    passOnFees: boolean;
    processingFee?: string;
    processingFeeTax?: string;
    serviceFee?: string;
    serviceFeeTax?: string;
    ticketRevenue?: string;
}

export const FREE_TICKET_TEXT = gettext('Fee per free ticket: Free');
export const PAID_TICKET_TEXT = gettext('Fee per paid ticket:');
export const CUSTOM_PRICING = gettext('Custom Pricing');

const SINGLE_TICKET_TYPE = gettext('Ticket types: 1');
const UNLIMITED_TICKET_TYPE = gettext('Ticket types: Unlimited');

const LOCALES_WITH_FEES_SWITCHED = [IE_LOCALE, GB_LOCALE];
const FREE_ITEM_PRICE = '0%';

export const pricingModulePackageDefaults = {
    [PACKAGE_ONE]: {
        name: PACKAGE_ONE_NAME,
        feePerFreeTicketText: FREE_TICKET_TEXT.toString(),
        feePerPaidTicketText: PAID_TICKET_TEXT.toString(),
        ticketTypesText: SINGLE_TICKET_TYPE.toString(),
    },
    [PACKAGE_TWO]: {
        name: PACKAGE_TWO_NAME,
        feePerFreeTicketText: FREE_TICKET_TEXT.toString(),
        feePerPaidTicketText: PAID_TICKET_TEXT.toString(),
        ticketTypesText: UNLIMITED_TICKET_TYPE.toString(),
    },
    [PACKAGE_THREE]: {
        name: PACKAGE_THREE_NAME,
        feePerFreeTicketText: FREE_TICKET_TEXT.toString(),
        feePerPaidTicketText: PAID_TICKET_TEXT.toString(),
        ticketTypesText: UNLIMITED_TICKET_TYPE.toString(),
    },
};

// Code below was copied from https://github.com/eventbrite/eb-ui/blob/d23d55e2c8b56fa4756eb4dc38b74cbddd8a451a/packages/organizer/assortments/src/reducers/util.js
const TRAILING_ZERO_REGEX = /((\.0*)|0)(?![0-9])/g;
const _trimZeros = (percent: string) =>
    percent.replace(TRAILING_ZERO_REGEX, '');

const _getformattedPercentWithFixed = (
    fixedFee: number,
    percentFee: number,
    locale: string,
) => {
    const fees = [
        ...(percentFee ? [percentFee] : []),
        ...(fixedFee ? [fixedFee] : []),
    ];

    if (LOCALES_WITH_FEES_SWITCHED.includes(locale)) {
        fees.reverse();
    }

    const result = fees.join(' + ');

    return result
        ? // TODO EB-163654 - Incorrect usage of gettext. Please refer to the Jira ticket for more information.
          // eslint-disable-next-line gettext/no-variable-string
          gettext(result, { percentFee, fixedFee }).toString()
        : FREE_ITEM_PRICE;
};

export interface PricingInterface {
    currency: string;
    feePercent: string;
    feeFixed: number;
}

export const formatPricing = (
    currencyFormat: string,
    locale: string,
    pricing: PricingInterface | null,
): string | null => {
    if (!pricing) return null;
    const { currency, feePercent, feeFixed } = pricing;
    let result = null;
    let formattedFixedFee = null;
    let formattedPercentFee: $FixMe = null;

    // e.g. 1% + 0.99
    // return null if we don't get any pricing data
    if (feePercent) {
        const trimmedPercentFee = _trimZeros(
            formatNumber(feePercent, currencyFormat),
        );

        formattedPercentFee = gettext('%(trimmedPercentFee)s%', {
            trimmedPercentFee,
        }).toString();
    }

    if (feeFixed) {
        formattedFixedFee = formatMajorMoney(
            feeFixed,
            currency,
            currencyFormat,
        );
    }

    result = _getformattedPercentWithFixed(
        formattedFixedFee,
        formattedPercentFee,
        locale,
    );

    /* We are gonna use USD for these countries, as PayPal doesn't accept their currencies
        We should be explicit that they are gonna charge fees in US dollars */
    const usdExtraCountries = [CL_LOCALE, CO_LOCALE, PE_LOCALE];

    if (usdExtraCountries.includes(locale)) {
        result = result.replace('$', 'US$');
    }

    return result;
};
